import axios from 'axios';
import baseUrl from './baseUrl';
import {USER_BASE} from './paths';
import {message} from 'antd';
import { getSessionInfo } from '../utils/sessionUtils';

const timeoutMs = Number(process.env.REACT_APP_HTTP_TIMEOUT_MS || 120000);
const REFRESH_ENDPOINT = `${USER_BASE}/refresh`;
const REFRESH_SKEW_MS = 5000;
const ACCESS_TOKEN_SKEW_MS = 3000;

const http = axios.create({
  baseURL: baseUrl,
  timeout: timeoutMs,
});

const refreshClient = axios.create({
  baseURL: baseUrl,
  timeout: timeoutMs,
});

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

export const getStoredToken = () => safeParse(localStorage.getItem('userToken'));

export const saveStoredToken = (data) => {
  if (!data) return;
  try {
    localStorage.setItem('userToken', JSON.stringify(data));
  } catch (_) { /* ignore */ }
};

export const clearStoredToken = () => {
  try {
    localStorage.removeItem('userToken');
  } catch (_) { /* ignore */ }
};

const buildAuthValue = (tokenObj) => `${tokenObj.tokenType || 'Bearer'} ${tokenObj.token}`;

const isTimestampExpired = (value, skewMs = 0) => {
  if (!value) return true;
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return true;
  return ts - skewMs <= Date.now();
};

let refreshPromise = null;

export const dispatchTokenRefreshed = (payload) => {
  try {
    window.dispatchEvent(new CustomEvent('token-refreshed', { detail: payload }));
  } catch (_) { /* ignore */ }
};

const attemptRefresh = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const current = getStoredToken();
    if (!current?.refreshToken) {
      return null;
    }
    if (isTimestampExpired(current.refreshExpiresAt, REFRESH_SKEW_MS)) {
      clearStoredToken();
      return null;
    }

    // 세션 정보 포함
    const sessionInfo = getSessionInfo();
    const response = await refreshClient.post(REFRESH_ENDPOINT, { 
      refreshToken: current.refreshToken,
      sessionId: sessionInfo.sessionId,
      deviceName: sessionInfo.deviceName
    });
    const data = response?.data?.data;
    if (!data?.token) {
      return null;
    }
    const merged = { ...current, ...data };
    if (current?.guest === true) {
      merged.guest = true;
    }
    saveStoredToken(merged);
    dispatchTokenRefreshed(merged);
    return merged;
  })()
    .catch((error) => {
      clearStoredToken();
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

export const ensureFreshToken = async ({ forceRefresh = false } = {}) => {
  const current = getStoredToken();
  if (!current) {
    return null;
  }

  const hasRefreshToken = !!current.refreshToken && !isTimestampExpired(current.refreshExpiresAt, REFRESH_SKEW_MS);
  const accessExpired = forceRefresh || !current.token || isTimestampExpired(current.expiresAt, ACCESS_TOKEN_SKEW_MS);

  if (accessExpired) {
    if (!hasRefreshToken) {
      return null;
    }
    try {
      const refreshed = await attemptRefresh();
      if (refreshed?.token) {
        return refreshed;
      }
      return getStoredToken();
    } catch (_) {
      return null;
    }
  }

  return current;
};

// 요청 인터셉터: 저장된 토큰 자동 헤더 첨부 (옵션 스킵 지원)
http.interceptors.request.use(async (config) => {
  if (config?.__skipAuthorization || config?.__isRefreshRequest) {
    return config;
  }

  let tokenObj = getStoredToken();
  if (tokenObj) {
    const ensured = await ensureFreshToken();
    if (ensured?.token) {
      tokenObj = ensured;
    } else if (!ensured) {
      tokenObj = null;
    }
  }

  if (tokenObj?.token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = buildAuthValue(tokenObj);
  }

  return config;
}, (error) => Promise.reject(error));

let notifying401 = false;
let lastErrorMsg = '';
let lastErrorAt = 0;
const ERROR_COOLDOWN_MS = 1200;

const notifyAuthRequired = (detail) => {
  if (notifying401) return;
  notifying401 = true;
  try {
    window.dispatchEvent(new CustomEvent('auth-required', { detail }));
  } catch (_) { /* ignore */ }
  setTimeout(() => { notifying401 = false; }, 1500);
};

// 응답 에러 공통 처리: 401 시 자동 토큰 갱신, 실패 시 이벤트 발행
http.interceptors.response.use(r => r, async (error) => {
  const status = error?.response?.status;
  const body = error?.response?.data;
  const backendMsg = body?.message;
  const originalRequest = error?.config || {};

  if (status === 401) {
    if (!originalRequest.__isRefreshRequest && !originalRequest.__skipAuthorization && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshed = await attemptRefresh();
        if (refreshed?.token) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = buildAuthValue(refreshed);
          delete originalRequest.__skipAuthorization;
          return http(originalRequest);
        }
      } catch (_) {
        // refresh failure falls through to auth-required
      }
    }

    clearStoredToken();
    notifyAuthRequired({ code: body?.code, message: backendMsg });
    return Promise.reject(error);
  }

  const now = Date.now();
  const silent = originalRequest?.__silent;
  if (!silent && backendMsg) {
    if (backendMsg !== lastErrorMsg || (now - lastErrorAt) > ERROR_COOLDOWN_MS) {
      message.error(backendMsg);
      lastErrorMsg = backendMsg;
      lastErrorAt = now;
    }
  }
  return Promise.reject(error);
});

// 응답 정규화 유틸: axios response | {data} | Array<any> 모두 수용
export const pickData = (res) => {
  if (res == null) return res;
  if (Array.isArray(res)) return res;
  if (typeof res === 'object') {
    const d = res.data;
    if (Array.isArray(d)) return d;
    if (d && typeof d === 'object' && 'data' in d) return d.data;
    return d ?? res;
  }
  return res;
};

export const pickList = (res) => {
  const d = pickData(res);
  return Array.isArray(d) ? d : [];
};

export default http;
